import React, { useState, useEffect } from 'react';
import { X, Plus, DollarSign, Package, AlertCircle, CheckCircle } from 'lucide-react';
import { getCatalogs, updateCatalog } from '../../lib/api';
import { toast } from '../../lib/toast';
import { useEscapeKey } from '../../hooks/useKeyboardShortcuts';

interface Product {
  _id: string;
  drugName: string;
  concentration: string;
  format: string;
  size: string;
  displayName: string;
  basePrice?: number;
  price?: number; // Catalog price (used when basePrice is stripped for non-admin)
}

interface Catalog {
  _id: string;
  catalogName: string;
  description?: string;
  products?: Array<{
    productId: string;
    price: number;
    notes?: string;
  }>;
}

interface AddToCatalogModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  onSuccess?: () => void;
}

const AddToCatalogModal: React.FC<AddToCatalogModalProps> = ({
  isOpen,
  onClose,
  product,
  onSuccess
}) => {
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string>('');
  const [price, setPrice] = useState<string>((product.basePrice ?? product.price ?? 0).toString());
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // ESC key to close modal
  useEscapeKey(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      loadCatalogs();
      setPrice((product.basePrice ?? product.price ?? 0).toString());
      setNotes('');
      setSelectedCatalogId('');
    }
  }, [isOpen, product]);

  const loadCatalogs = async () => {
    setLoading(true);
    try {
      const result = await getCatalogs();
      if (result.success && result.data) {
        setCatalogs(result.data);
      } else {
        toast.error(result.error || 'Failed to load catalogs');
      }
    } catch (error) {
      toast.error('Failed to load catalogs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCatalogId) {
      toast.error('Please select a catalog');
      return;
    }

    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setSubmitting(true);
    try {
      const catalog = catalogs.find(c => c._id === selectedCatalogId);
      if (!catalog) {
        toast.error('Catalog not found');
        return;
      }

      // Check if product already exists in catalog
      const existingProducts = catalog.products || [];
      const productExists = existingProducts.some(p => p.productId === product._id);

      if (productExists) {
        toast.error('Product already exists in this catalog');
        setSubmitting(false);
        return;
      }

      // Add the new product to the existing products
      const updatedProducts = [
        ...existingProducts,
        {
          productId: product._id,
          price: priceNum,
          notes: notes || undefined
        }
      ];

      const result = await updateCatalog(selectedCatalogId, {
        products: updatedProducts
      });

      if (result.success) {
        toast.success('Product added to catalog successfully');
        onSuccess?.();
        onClose();
      } else {
        toast.error(result.error || 'Failed to add product to catalog');
      }
    } catch (error) {
      toast.error('Failed to add product to catalog');
    } finally {
      setSubmitting(false);
    }
  };

  const getProductCountInCatalog = (catalog: Catalog) => {
    const isInCatalog = catalog.products?.some(p => p.productId === product._id);
    const productCount = catalog.products?.length || 0;
    return { isInCatalog, productCount };
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Add to Catalog</h2>
            <p className="text-sm text-gray-500 mt-1">
              Add "{product.displayName}" to one of your catalogs
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : catalogs.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 mb-2">No catalogs available</p>
              <p className="text-sm text-gray-400">Create a catalog first to add products</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Product Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">{product.displayName}</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><span className="font-medium">Drug:</span> {product.drugName}</p>
                  <p><span className="font-medium">Format:</span> {product.format}</p>
                  <p><span className="font-medium">Size:</span> {product.size}</p>
                  {product.basePrice !== undefined && (
                    <p><span className="font-medium">Base Price:</span> ${product.basePrice.toFixed(2)}</p>
                  )}
                </div>
              </div>

              {/* Catalog Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Catalog *
                </label>
                <div className="space-y-2">
                  {catalogs.map((catalog) => {
                    const { isInCatalog, productCount } = getProductCountInCatalog(catalog);
                    return (
                      <div
                        key={catalog._id}
                        onClick={() => !isInCatalog && setSelectedCatalogId(catalog._id)}
                        className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                          isInCatalog
                            ? 'bg-gray-50 border-gray-300 opacity-50 cursor-not-allowed'
                            : selectedCatalogId === catalog._id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-gray-900">{catalog.catalogName}</h4>
                              {isInCatalog && (
                                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                  <CheckCircle className="h-3 w-3" />
                                  Already added
                                </span>
                              )}
                            </div>
                            {catalog.description && (
                              <p className="text-sm text-gray-600 mt-1">{catalog.description}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-2">
                              {productCount} product{productCount !== 1 ? 's' : ''}
                            </p>
                          </div>
                          {selectedCatalogId === catalog._id && !isInCatalog && (
                            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center ml-3 flex-shrink-0">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Price Input */}
              {selectedCatalogId && (
                <>
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                      Price in Catalog *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        id="price"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        min="0"
                        step="0.01"
                        required
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                    {product.basePrice !== undefined && (
                      <p className="mt-1 text-xs text-gray-500">
                        Base price: ${product.basePrice.toFixed(2)}
                      </p>
                    )}
                  </div>

                  {/* Notes Input */}
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      maxLength={200}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Add any notes about this product in the catalog..."
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {notes.length}/200 characters
                    </p>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      This product will be added to the catalog and will be available to all physicians assigned to this catalog.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedCatalogId || submitting || loading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Adding...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Add to Catalog
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToCatalogModal;

